import { applyWorkspaceContextCap } from "@/lib/buildConversationWorkspaceDocuments";
import { flattenDocs } from "@/models/workspace/utils";
import Workspace from "@/models/workspace/Workspace";
import type {
  DocumentIdWithRevisionNumber,
  WorkspaceContext,
} from "@/services/messageService";
import { collectFolderAndDocumentIds } from "../utils/PineconeQueries";

function allItemIdsForWorkspace(ws: Workspace): string[] {
  return [
    ...ws.documents.map((doc) => doc.id),
    ...collectFolderAndDocumentIds(ws.folders),
  ];
}

function normalizeContextEntry(
  ws: Workspace,
  selectedIds: (
    | string
    | { documentId: string; activeRevisionNumber?: number }
  )[]
): DocumentIdWithRevisionNumber[] {
  const allItemIdsSet = new Set(allItemIdsForWorkspace(ws));
  const docs = flattenDocs(ws);
  const docById = new Map(docs.map((d) => [d.id, d]));

  return selectedIds
    .map((sel) => (typeof sel === "string" ? sel : sel.documentId))
    .filter((id) => allItemIdsSet.has(id))
    .map((id) => {
      const doc = docById.get(id);
      if (!doc) return undefined as unknown as DocumentIdWithRevisionNumber;
      return {
        documentId: doc.id,
        activeRevisionNumber: doc.activeRevision,
      } as DocumentIdWithRevisionNumber;
    })
    .filter((v): v is DocumentIdWithRevisionNumber => Boolean(v));
}

function cleanupWorkspaceContext(
  workspaces: Workspace[],
  context: Record<string, DocumentIdWithRevisionNumber[]>
): {
  newContext: Record<string, DocumentIdWithRevisionNumber[]>;
  needsCleanup: boolean;
} {
  const availableIds = workspaces.map((ws) => ws.id);
  let needsCleanup = false;
  const newContext: Record<string, DocumentIdWithRevisionNumber[]> = {
    ...context,
  };

  // Remove entries for deleted workspaces
  for (const wsId in context) {
    if (!availableIds.includes(wsId)) {
      delete newContext[wsId];
      needsCleanup = true;
    }
  }

  // Remove references to deleted documents within existing workspaces and normalize shape
  for (const wsId in newContext) {
    const selected = newContext[wsId] as unknown as (
      | string
      | { documentId: string; activeRevisionNumber?: number }
    )[];
    const ws = workspaces.find((w) => w.id === wsId);
    if (!ws) continue;

    const normalized = normalizeContextEntry(ws, selected);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (normalized.length !== (newContext[wsId] as unknown as any[]).length) {
      needsCleanup = true;
    }
    if (normalized.length === 0) {
      delete newContext[wsId];
      needsCleanup = true;
    } else {
      newContext[wsId] = normalized;
    }
  }

  return { newContext, needsCleanup };
}

function computeConversationFlags(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedConversation: any,
  workspaces: Workspace[]
): {
  isWorkspaceDeleted: boolean;
  isWorkspaceDocumentsMissing: boolean;
  isContentOutdated: boolean;
} {
  if (!selectedConversation) {
    return {
      isWorkspaceDeleted: false,
      isWorkspaceDocumentsMissing: false,
      isContentOutdated: false,
    };
  }

  const availableIds = workspaces.map((ws) => ws.id);
  const conversationWorkspaceIds = selectedConversation.workspaceFilter.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (f: any) => f.workspaceId
  );
  const deletedWorkspaceIds = conversationWorkspaceIds.filter(
    (id: string) => !availableIds.includes(id)
  );

  if (deletedWorkspaceIds.length > 0) {
    return {
      isWorkspaceDeleted: true,
      isWorkspaceDocumentsMissing: false,
      isContentOutdated: false,
    };
  }

  // Deleted documents check (revision-aware by id)
  for (const filter of selectedConversation.workspaceFilter) {
    const ws = workspaces.find((w) => w.id === filter.workspaceId);
    if (!ws) continue;

    const allItemIdsSet = new Set(allItemIdsForWorkspace(ws));
    const referencedDocIds: string[] =
      filter.documentIdsWithRevisionNumbers?.map(
        (x: DocumentIdWithRevisionNumber) => x.documentId
      ) ??
      filter.documentIds ??
      [];

    if (referencedDocIds.some((docId) => !allItemIdsSet.has(docId))) {
      return {
        isWorkspaceDeleted: false,
        isWorkspaceDocumentsMissing: true,
        isContentOutdated: false,
      };
    }
  }

  // Outdated ONLY if ACTIVE revision changed (ignore non-active newer revisions/timestamps)
  const isOutdated = selectedConversation.workspaceFilter.some(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (filter: any) => {
      const ws = workspaces.find((w) => w.id === filter.workspaceId);
      if (!ws) return false;

      const docs = flattenDocs(ws);
      if (docs.length === 0) return false;

      const referenced: DocumentIdWithRevisionNumber[] =
        filter.documentIdsWithRevisionNumbers ??
        (filter.documentIds ?? []).map((id: string) => ({ documentId: id }));
      if (referenced.length === 0) return false;

      const refIds = new Set(referenced.map((r) => r.documentId));
      const refById = new Map(referenced.map((r) => [r.documentId, r]));

      const filteredDocs = docs.filter((doc) => refIds.has(doc.id));
      if (filteredDocs.length === 0) return false;

      return filteredDocs.some((doc) => {
        const ref = refById.get(doc.id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const currentActive = (doc as any).activeRevision;
        const referencedActive = ref?.activeRevisionNumber;
        return (
          typeof currentActive === "number" &&
          typeof referencedActive === "number" &&
          currentActive !== referencedActive
        );
      });
    }
  );

  return {
    isWorkspaceDeleted: false,
    isWorkspaceDocumentsMissing: false,
    isContentOutdated: isOutdated,
  };
}

function initOrMergeContextForWorkspace(
  workspaceId: string,
  workspaces: Workspace[],
  context: Record<string, DocumentIdWithRevisionNumber[]>
): {
  updatedContext: Record<string, DocumentIdWithRevisionNumber[]>;
  changed: boolean;
  trimmedAway: number;
} {
  const ws = workspaces.find((w) => w.id === workspaceId);
  if (!ws)
    return { updatedContext: context, changed: false, trimmedAway: 0 };

  const updated = { ...context } as Record<
    string,
    DocumentIdWithRevisionNumber[]
  >;

  if (!updated[workspaceId]) {
    const docs = flattenDocs(ws);
    updated[workspaceId] = docs.map((doc) => ({
      documentId: doc.id,
      activeRevisionNumber: doc.activeRevision,
    }));
    const capped = applyWorkspaceContextCap(updated as WorkspaceContext, workspaces);
    return {
      updatedContext: capped.context as Record<
        string,
        DocumentIdWithRevisionNumber[]
      >,
      changed: true,
      trimmedAway: capped.trimmedAway,
    };
  }

  const existing = updated[workspaceId] as DocumentIdWithRevisionNumber[];
  const byId = new Map(existing.map((e) => [e.documentId, e]));
  const docs = flattenDocs(ws);
  for (const doc of docs) {
    byId.set(doc.id, {
      documentId: doc.id,
      activeRevisionNumber: doc.activeRevision,
    });
  }
  updated[workspaceId] = Array.from(byId.values());
  const capped = applyWorkspaceContextCap(updated as WorkspaceContext, workspaces);
  return {
    updatedContext: capped.context as Record<
      string,
      DocumentIdWithRevisionNumber[]
    >,
    changed: true,
    trimmedAway: capped.trimmedAway,
  };
}

export {
  allItemIdsForWorkspace,
  normalizeContextEntry,
  cleanupWorkspaceContext,
  computeConversationFlags,
  initOrMergeContextForWorkspace,
};
